import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import type { DbEntity } from "../../../generated/prisma-models";
import {
  DEFAULT_TEMPERATURE,
  TEMPERATURE_MAX,
  TEMPERATURE_MIN
} from "../../../models/model-catalog.config";
import {
  ClassificationPromptConfigSchema,
  ExtractionPromptConfigSchema,
  GenerationPromptConfigSchema,
  SummarisationPromptConfigSchema,
  TranslationPromptConfigSchema
} from "../../../schemas/AITask/promptGenerationConfig/index";
import { ProviderTuningShape } from "../../../schemas/ModelConfig/provider-tuning";

// Prisma type for AITask with fewShots included (from staging Few-Shots feature)
export type AITaskWithFewShots = DbEntity.AITask & Pick<DbEntity.AITaskRelations, "fewShots">;

const baseSchema = z.object({
  name: z.string().min(1, "Task name is required"),
  description: z.string().optional(),
  category: z.nativeEnum(DbEnum.AgentTaskCategory),
  temperature: z.number().min(TEMPERATURE_MIN).max(TEMPERATURE_MAX).default(DEFAULT_TEMPERATURE),
  modelName: z.string().optional(),
  modelProvider: z.nativeEnum(DbEnum.ModelProvider).optional(),
  // Spread from the canonical shape, never restated. This schema is one of the
  // write paths that silently dropped the Kimi effort level by omitting it from
  // its own copy of the list; `ProviderTuningShape` carries the account.
  ...ProviderTuningShape,
  // BYOM selector — the id of a CustomModel row, carried into the stored
  // `modelConfig` by `buildModelConfig`. Kept as a bare string to match
  // `ModelConfigSchema`, which reads the same key back at inference; the v1
  // write bound is `CustomModelIdSchema` and is deliberately stricter.
  customModelId: z.string().optional(),
  prompt: z.string().optional(),
  inputFormat: z.enum(["text", "json"]).default("text"),
  outputFormat: z.enum(["text", "json", "template"]).default("text")
});

// DB schemas are the source of truth — _v is preserved so it flows
// through to the DB. Form-specific defaults added where needed.
const generationSchema = GenerationPromptConfigSchema;

const extractionSchema = ExtractionPromptConfigSchema;

const translationSchema = TranslationPromptConfigSchema.extend({
  respectLevel: z.number().min(0).max(1).optional().default(0.5)
});

const summarisationSchema = SummarisationPromptConfigSchema;

const classificationSchema = ClassificationPromptConfigSchema;

/**
 * EXPORTED so a PARTIAL update can be derived from it rather than restated.
 *
 * `ZTasks.UpdateDetails` picks `name` and `description` off this object and
 * marks them optional. Writing those two field rules out again in the contract
 * would be a second source of truth that agrees today and drifts on the first
 * change to either — the drift `types-system.md` exists to forbid. Deriving
 * them means a bound changed here reaches the partial update for free.
 */
export const taskFormSchemaBase = z.object({
  ...baseSchema.shape,
  generation: generationSchema.optional(),
  classification: classificationSchema.optional(),
  extraction: extractionSchema.optional(),
  translation: translationSchema.optional(),
  summarisation: summarisationSchema.optional()
});

type TaskFormData = z.output<typeof taskFormSchemaBase>;

const sanitizeTaskFormData = <T extends TaskFormData>(data: T): T => {
  if (data.category !== DbEnum.AgentTaskCategory.GENERATION) {
    return data;
  }

  const cleanedData = { ...data };

  if (cleanedData.generation) {
    const generation = { ...cleanedData.generation };

    // Handle input format - only one field should exist
    if (data.inputFormat === "text") {
      generation.jsonInputSchema = {};
    } else if (data.inputFormat === "json") {
      generation.expectedInput = "";
    }

    // Handle output format - only one field should exist
    if (data.outputFormat === "text") {
      generation.jsonOutputSchema = {};
      generation.documentTemplateId = undefined;
    } else if (data.outputFormat === "json") {
      generation.expectedOutput = "";
      generation.documentTemplateId = undefined;
    } else if (data.outputFormat === "template") {
      generation.expectedOutput = "";
      generation.jsonOutputSchema = {};
    }

    cleanedData.generation = generation;
  }

  return cleanedData;
};

const refiner = (data: TaskFormData, ctx: z.RefinementCtx) => {
  if (data.category === DbEnum.AgentTaskCategory.GENERATION) {
    const generation = data.generation;

    if (!generation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Generation data is required for generation tasks",
        path: ["generation"]
      });
      return;
    }

    if (data.inputFormat === "json") {
      if (
        !generation.jsonInputSchema ||
        (typeof generation.jsonInputSchema === "object" &&
          Object.keys(generation.jsonInputSchema).length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Input schema is required when input format is JSON",
          path: ["generation", "jsonInputSchema"]
        });
      }
    }

    if (data.outputFormat === "json") {
      if (
        !generation.jsonOutputSchema ||
        (typeof generation.jsonOutputSchema === "object" &&
          Object.keys(generation.jsonOutputSchema).length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Output schema is required when output format is JSON",
          path: ["generation", "jsonOutputSchema"]
        });
      }
    } else if (data.outputFormat === "template") {
      if (!generation.documentTemplateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Document template is required when output format is Template",
          path: ["generation", "documentTemplateId"]
        });
      }
    }
  }

  if (data.category === DbEnum.AgentTaskCategory.EXTRACTION) {
    const extraction = data.extraction;
    // If !extraction.expectedData, or the object is empty, add an error
    if (!extraction?.expectedData || Object.keys(extraction.expectedData).length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expected data is required for extraction tasks",
        path: ["extraction", "expectedData"]
      });
    }
  }
};

const validateGenerationTextFormat = (data: TaskFormData, ctx: z.RefinementCtx) => {
  if (data.category !== DbEnum.AgentTaskCategory.GENERATION) {
    return;
  }

  const generation = data.generation;

  if (data.inputFormat === "text") {
    if (!generation?.expectedInput || generation.expectedInput.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expected input is required when input format is Text",
        path: ["generation", "expectedInput"]
      });
    }
  }

  if (data.outputFormat === "text") {
    if (!generation?.expectedOutput || generation.expectedOutput.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expected output is required when input format is Text",
        path: ["generation", "expectedOutput"]
      });
    }
  }
};

export const taskFormSchema = taskFormSchemaBase
  .transform(sanitizeTaskFormData)
  .superRefine((data, ctx) => {
    refiner(data, ctx);
    validateGenerationTextFormat(data, ctx);
  });

export type TaskFormSchemaType = z.infer<typeof taskFormSchema>;

// THIS IS THE SCHEMA THAT IS USED FOR THE FORM BY THE BACKEND WHEN CREATING A TASK
export const taskCreationSchemaBase = taskFormSchemaBase
  .omit({ modelName: true, modelProvider: true })
  .merge(
    z.object({
      modelName: z.string().min(1, "Model name is required"),
      modelProvider: z.nativeEnum(DbEnum.ModelProvider)
    })
  );

export const taskCreationSchema = taskCreationSchemaBase
  .transform(sanitizeTaskFormData)
  .superRefine((data, ctx) => {
    refiner(data, ctx);
  });

export type TaskCreationSchemaType = z.infer<typeof taskCreationSchemaBase>;
